import * as React from 'react';
import { useMediaQuery } from 'react-responsive';

import { useConfig } from './ConfigProvider';

import { FeatureOverrideProvider, IFeatures } from './FeatureProvider';
import { StringsOverrideProvider, IStringsContext } from './StringsProvider';
import { ThemeOverrideProvider, IThemeContext } from './ThemeProvider';

import { InterstitialTypes } from 'src/types/IModal';

interface IExperimentProviderProps {
  children: React.ReactNode;
}

export function ExperimentProvider(props: IExperimentProviderProps) {
  const { experiments } = useConfig();
  const { children } = props;
  const [initialized, setInitialized] = React.useState(false);
  const [checkboxCartEnabled, setCheckboxCartEnabled] = React.useState(false);

  const [featureOverrides, setFeatureOverrides] = React.useState(
    {} as Partial<IFeatures>
  );
  const [stringsOverrides, setStringsOverrides] = React.useState(
    {} as Partial<IStringsContext>
  );
  const [themeOverrides, setThemeOverrides] = React.useState(
    {} as Partial<IThemeContext>
  );

  // TODO: Enable multiple experiments to run at once.
  const activeExperiment = React.useMemo(
    () => experiments && experiments[0],
    [experiments]
  );

  React.useEffect(() => {
    if (initialized) {
      return;
    }
    // EXPERIMENT NOTES:
    //  - Be very explicit which overrides an experiment is enabling / disabling.
    //  - DO NOT set any override to `false` unless it's actually affected by the experiment.
    //  - If it's not obvious what the experiment is doing by name, please leave a short comment explaining it.
    switch (activeExperiment) {
      case 'test-experiments':
        // Experiment to test the experiments feature
        setFeatureOverrides({
          mostPopularBadge: false,
          prioritizeTermsPosition: true,
          showDeclineOption: true,
        });
        break;
      case 'show-ipid-disclaimer':
        setFeatureOverrides({
          showIpidDisclaimer: true,
        });
        break;
      case 'whiteoutq4test':
        // Changes header and footer color in prompt to white
        setThemeOverrides({
          promptCopyAndSvgColor: '--white',
        });
        break;
      case 'force-visual-checkboxes-q42022':
        // Forces checkbox inputs to look like checkboxes instead of radio buttons
        setFeatureOverrides({ forceVisualCheckboxes: true });
        break;
      case 'randomize-testimonial':
        setFeatureOverrides({ showTestimonial: true });
        break;
      case 'slide-out-interstitial-cta':
        // Replaces the Modal CTA with the Slide-Out CTA
        setFeatureOverrides({
          interstitialType: InterstitialTypes.SLIDEOUT,
          showPriceInButton: false,
        });
        setStringsOverrides({
          addProtectionButtonLabel: 'Add Protection Plan',
          declineButtonLabel: 'No Thanks',
        });
        setThemeOverrides({ fullWidthAddAndDeclineProtection: true });
        break;
      case 'dropdown-modal':
        setFeatureOverrides({ reducedDecisionModal: true });
        break;
      case 'best_value_flag':
        setFeatureOverrides({ bestValueBadge: true });
        break;
      case 'checkbox-cart':
        // NOTE: this is hacky just for this experiment, but the content height observer
        // gets very confused if we originally render with cartType=dropdown. So the first render
        // pass needs to have the correct cartType. I'd love to find a better way to do this.
        setFeatureOverrides({ cartType: 'checkbox' });
        setCheckboxCartEnabled(true);
        break;
      case 'modal-brand-image':
        // Experiment to add brand image to desktop modal CTA
        setFeatureOverrides({ showModalBrandImage: true });
        break;
      default:
        break;
    }
    setInitialized(true);
  }, [initialized, setInitialized, activeExperiment, setFeatureOverrides, setCheckboxCartEnabled]);

  // Toggle checkbox cart to simple if we don't have enough space
  const isWideEnoughForCheckboxCart = useMediaQuery({ query: '(min-width: 270px)' });
  React.useEffect(() => {
    if (checkboxCartEnabled) {
      setFeatureOverrides((prevFeatures) => ({
        ...prevFeatures,
        cartType: isWideEnoughForCheckboxCart ? 'checkbox' : 'simple',
      }));
    }
  }, [setFeatureOverrides, checkboxCartEnabled, isWideEnoughForCheckboxCart]);

  if (!initialized) {
    return null;
  }

  return (
    <FeatureOverrideProvider {...featureOverrides}>
      <ThemeOverrideProvider {...themeOverrides}>
        <StringsOverrideProvider {...stringsOverrides}>
          {children}
        </StringsOverrideProvider>
      </ThemeOverrideProvider>
    </FeatureOverrideProvider>
  );
}
